import React, { useEffect, useState, useContext, Suspense } from "react";
import { BrowserRouter, Switch, useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import moment from "moment";

import LoggedInLayout from "../layout";
import Signup from "../pages/Signup";
import Login from "../pages/Login/";
import JivoPageTracker from "../components/JivoPageTracker";
import { AuthProvider, AuthContext } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { ConnectionProvider } from "../context/Connection/ConnectionContext";
import { SidebarLayoutProvider } from "../layout/Sidebar/SidebarContext";
import Route from "./Route";
import lazyWithReload from "../helpers/lazyWithReload";
import { startAppUpdater } from "../helpers/appUpdater";

// Every authenticated page is code-split: only Login/Signup stay eager,
// since they're the first screen an unauthenticated visitor hits.
const Dashboard = lazyWithReload(() => import("../pages/Dashboard/"));
const TicketResponsiveContainer = lazyWithReload(() => import("../pages/TicketResponsiveContainer"));
const Connections = lazyWithReload(() => import("../pages/Connections/"));
const Settings = lazyWithReload(() => import("../pages/Settings/"));
const Financeiro = lazyWithReload(() => import("../pages/Financeiro/"));
const Users = lazyWithReload(() => import("../pages/Users"));
const Contacts = lazyWithReload(() => import("../pages/Contacts/"));
const ContactImportPage = lazyWithReload(() => import("../pages/Contacts/import"));
const ChatMoments = lazyWithReload(() => import("../pages/Moments"));
const Queues = lazyWithReload(() => import("../pages/Queues/"));
const Tags = lazyWithReload(() => import("../pages/Tags/"));
const Products = lazyWithReload(() => import("../pages/Products/"));
const Orders = lazyWithReload(() => import("../pages/Orders/"));
const MessagesAPI = lazyWithReload(() => import("../pages/MessagesAPI/"));
const Helps = lazyWithReload(() => import("../pages/Helps/"));
const ContactLists = lazyWithReload(() => import("../pages/ContactLists/"));
const ContactListItems = lazyWithReload(() => import("../pages/ContactListItems/"));
const Companies = lazyWithReload(() => import("../pages/Companies/"));
const AuditLogs = lazyWithReload(() => import("../pages/AuditLogs/"));
const Wallets = lazyWithReload(() => import("../pages/Wallets/"));
const QuickMessages = lazyWithReload(() => import("../pages/QuickMessages/"));
const Schedules = lazyWithReload(() => import("../pages/Schedules"));
const Campaigns = lazyWithReload(() => import("../pages/Campaigns"));
const CampaignsConfig = lazyWithReload(() => import("../pages/CampaignsConfig"));
const CampaignReport = lazyWithReload(() => import("../pages/CampaignReport"));
const Annoucements = lazyWithReload(() => import("../pages/Annoucements"));
const Chat = lazyWithReload(() => import("../pages/Chat"));
const Prompts = lazyWithReload(() => import("../pages/Prompts"));
const AllConnections = lazyWithReload(() => import("../pages/AllConnections/"));
const Reports = lazyWithReload(() => import("../pages/Reports"));
const RelatorioVendas = lazyWithReload(() => import("../pages/RelatorioVendas"));
const QueueIntegration = lazyWithReload(() => import("../pages/QueueIntegration"));
const Files = lazyWithReload(() => import("../pages/Files/"));
const ToDoList = lazyWithReload(() => import("../pages/ToDoList/"));
const Kanban = lazyWithReload(() => import("../pages/Kanban"));
const TagsKanban = lazyWithReload(() => import("../pages/TagsKanban"));
// aniversário desativado por enquanto
// const BirthdaySettingsPage = lazyWithReload(() => import("../pages/BirthdaySettings"));
const CallHistoricals = lazyWithReload(() => import("../pages/CallHistoricals"));
const FlowBuilderConfig = lazyWithReload(() =>
  import("../pages/FlowBuilderConfig").then((m) => ({ default: m.FlowBuilderConfig }))
);
const FlowBuilder = lazyWithReload(() => import("../pages/FlowBuilder"));
const CampaignsPhrase = lazyWithReload(() => import("../pages/CampaignsPhrase"));

const RoutesContent = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);
  const { user } = useContext(AuthContext);
  const history = useHistory();

  useEffect(() => {
    startAppUpdater(history);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  // Verificar se a empresa está vencida
  const isCompanyExpired = () => {
    if (!user || user.profile === "root" || !user.company || user.company.id === 1) {
      return false; // Empresa ID 1 nunca expira, root nunca é bloqueado por vencimento
    }

    const dueDate = user.company.dueDate;
    if (!dueDate) return false;

    // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
    const hojeInicio = moment().startOf('day');
    const vencimentoInicio = moment(dueDate).startOf('day');
    
    // Empresa está vencida apenas após o dia do vencimento
    return hojeInicio.isAfter(vencimentoInicio, 'day');
  };

  return (
    <ConnectionProvider>
      <TicketsContextProvider>
      <Switch>
        <Route exact path="/login" component={Login} />
        <Route exact path="/signup" component={Signup} />
        <SidebarLayoutProvider>
        <WhatsAppsProvider>
          <LoggedInLayout hideMenu={isCompanyExpired()}>
              <Suspense fallback={null}>
                <Route
                  exact
                  path="/financeiro"
                  component={Financeiro}
                  isPrivate
                />

                <Route
                  exact
                  path="/financeiro-aberto"
                  component={Financeiro}
                  isPrivate
                />

                <Route
                  exact
                  path="/companies"
                  component={Companies}
                  isPrivate
                  requiredProfile="root"
                />
                <Route
                  exact
                  path="/audit-logs"
                  component={AuditLogs}
                  isPrivate
                  requiredProfile="root"
                />
                {/* aniversário desativado por enquanto
                <Route
                  exact
                  path="/birthday-settings"
                  component={BirthdaySettingsPage}
                  isPrivate
                />
                */}
                <Route exact path="/" component={Dashboard} isPrivate />
                <Route exact path="/call-historicals" component={CallHistoricals} isPrivate />
                <Route
                  exact
                  path="/tickets/:ticketId?"
                  component={TicketResponsiveContainer}
                  isPrivate
                />
                <Route
                  exact
                  path="/connections"
                  component={Connections}
                  isPrivate
                />
                <Route
                  exact
                  path="/quick-messages"
                  component={QuickMessages}
                  isPrivate
                />
                <Route exact path="/todolist" component={ToDoList} isPrivate />
                <Route
                  exact
                  path="/schedules"
                  component={Schedules}
                  isPrivate
                />
                <Route exact path="/tags" component={Tags} isPrivate />
                <Route exact path="/products" component={Products} isPrivate />
                <Route exact path="/orders" component={Orders} isPrivate />
                <Route exact path="/contacts" component={Contacts} isPrivate />
                <Route
                  exact
                  path="/contacts/import"
                  component={ContactImportPage}
                  isPrivate
                />
                <Route exact path="/wallets" component={Wallets} isPrivate />
                <Route exact path="/helps" component={Helps} isPrivate />
                <Route exact path="/users" component={Users} isPrivate />
                <Route
                  exact
                  path="/messages-api"
                  component={MessagesAPI}
                  isPrivate
                />
                <Route
                  exact
                  path="/settings"
                  component={Settings}
                  isPrivate
                  requiredProfile={["admin", "root"]}
                />
                <Route exact path="/queues" component={Queues} isPrivate />
                <Route exact path="/reports" component={Reports} isPrivate />
                <Route
                  exact
                  path="/relatorio-vendas"
                  component={RelatorioVendas}
                  isPrivate
                />
                <Route
                  exact
                  path="/queue-integration"
                  component={QueueIntegration}
                  isPrivate
                />
                <Route
                  exact
                  path="/announcements"
                  component={Annoucements}
                  isPrivate
                  requiredProfile="admin"
                />
                <Route exact path="/chats/:id?" component={Chat} isPrivate />
                <Route exact path="/files" component={Files} isPrivate />
                <Route
                  exact
                  path="/moments"
                  component={ChatMoments}
                  isPrivate
                />
                <Route exact path="/Kanban" component={Kanban} isPrivate />
                <Route
                  exact
                  path="/TagsKanban"
                  component={TagsKanban}
                  isPrivate
                />
                <Route exact path="/prompts" component={Prompts} isPrivate />
                <Route
                  exact
                  path="/allConnections"
                  component={AllConnections}
                  isPrivate
                  requiredProfile="root"
                />

                <Route
                  exact
                  path="/phrase-lists"
                  component={CampaignsPhrase}
                  isPrivate
                />
                <Route
                  exact
                  path="/flowbuilders"
                  component={FlowBuilder}
                  isPrivate
                />
                <Route
                  exact
                  path="/flowbuilder/:id?"
                  component={FlowBuilderConfig}
                  isPrivate
                />

                {showCampaigns && (
                  <>
                    <Route
                      exact
                      path="/contact-lists"
                      component={ContactLists}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/contact-lists/:contactListId/contacts"
                      component={ContactListItems}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns"
                      component={Campaigns}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaign/:campaignId/report"
                      component={CampaignReport}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns-config"
                      component={CampaignsConfig}
                      isPrivate
                    />
                  </>
                )}
              </Suspense>
              </LoggedInLayout>
            </WhatsAppsProvider>
            </SidebarLayoutProvider>
          </Switch>
          <ToastContainer position="top-center" autoClose={10000} />
        </TicketsContextProvider>
      </ConnectionProvider>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <JivoPageTracker />
      <AuthProvider>
        <RoutesContent />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
